import React from 'react'
import { Link } from '@tanstack/react-router'
import { englishRoute } from 'lib/dictionary-routes'
import { EngWordFullProps, EngWordBasicProps } from 'core/interfaces/dictionary-eng-interfaces'
import { WordTranslation } from '../../lib/interfaces'
import { Translit } from '../../lib/transliteration'
import WordWithTooltip from '../tooltip/word-with-tooltip'
import { useTranslit } from 'hooks/useTranslit'
import { markSearchSource } from 'lib/analytics/events'

type Props = {
   engWords: EngWordBasicProps[]
   translations: WordTranslation[]
}

const renderMeaningTlit = (
   translations: WordTranslation[],
   word: string,
   isLast: boolean,
   translit: Translit
) => {
   const wordTranslation = translations.find((t) => t.unspacedText === word)
   if (!wordTranslation) return null

   return (
      <React.Fragment key={wordTranslation.unspacedText}>
         <span className="font-translit">
            {wordTranslation.wordObjects.map((wo) => `${translit.of(wo.phonetic)} `)}
         </span>

         {!isLast && <span className="px-[5px] font-normal text-inherit">;</span>}
      </React.Fragment>
   )
}

const renderMeaningWord = (translations: WordTranslation[], word: string, isLast: boolean) => {
   const wordTranslation = translations.find((t) => t.unspacedText === word)

   if (!wordTranslation) return null

   return (
      <React.Fragment key={wordTranslation.unspacedText}>
         {wordTranslation.wordObjects.map((wo, index: number) => (
            <WordWithTooltip wordObject={wo} key={index} />
         ))}

         {!isLast && <span className="px-[7px] font-normal text-inherit">;</span>}
      </React.Fragment>
   )
}

const renderMeaning = (
   translations: WordTranslation[],
   engWord: EngWordFullProps,
   translit: Translit
) => {
   return (
      <div className="eng-meanings-thai-container">
         <span className="eng-meanings-thai thai-line">
            {engWord.meanings.map((meaning) => {
               const meaningWords = meaning.meaning.split(';').map((w) => w.trim())
               return meaningWords.map((word, index) => {
                  const isLast = index === meaningWords.length - 1
                  return renderMeaningWord(translations, word, isLast)
               })
            })}
         </span>
         {translit.enabled && (
            <div className="tlit uc">
               {engWord.meanings.map((meaning) => {
                  const meaningWords = meaning.meaning.split(';').map((w) => w.trim())

                  return meaningWords.map((word, index) => {
                     const isLast = index === meaningWords.length - 1
                     return renderMeaningTlit(translations, word, isLast, translit)
                  })
               })}
            </div>
         )}
      </div>
   )
}

const EnglishSearchResults = (props: Props) => {
   const { translations } = props
   const translit = useTranslit()

   return (
      <ul>
         {props.engWords.map((engWord: EngWordFullProps, wordIndex: number) => (
            <li
               className="flex flex-col lg:flex-row lg:items-center list-none p-[15px_25px] border-b border-b-[#ddd] odd:bg-listOdd first:mt-[20px] last:mb-0 last:border-b-0"
               key={wordIndex}
            >
               <div className="text-[24px] font-normal min-[30px]:min-w-[160px] min-[30px]:w-[160px] md:min-w-[380px] md:w-[380px] [&_a]:text-[#1f1f1f]">
                  <Link
                     {...englishRoute(engWord.word)}
                     onClick={() => markSearchSource('result_click')}
                  >
                     {engWord.word}
                  </Link>
               </div>
               {renderMeaning(translations, engWord, translit)}
            </li>
         ))}
      </ul>
   )
}

export default EnglishSearchResults
